export const DIE_FACES = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];
export const DICE_SPIN_MS = 2000;
const FACE_CYCLE_MS = 90;

/** @type {WeakMap<HTMLElement, Map<string, number>>} */
const spinIntervals = new WeakMap();

/** @type {WeakMap<HTMLElement, Set<string>>} */
const spinningPlayers = new WeakMap();

/**
 * @param {HTMLElement} root
 */
function getSpinningSet(root) {
  let set = spinningPlayers.get(root);
  if (!set) {
    set = new Set();
    spinningPlayers.set(root, set);
  }
  return set;
}

/**
 * @param {HTMLElement} root
 * @param {boolean} disabled
 */
function setRollButtonsDisabled(root, disabled) {
  root.querySelectorAll("[data-roll-player]").forEach((btn) => {
    /** @type {HTMLButtonElement} */ (btn).disabled = disabled;
  });
}

/**
 * @param {HTMLElement} root
 */
function syncRollButtons(root) {
  setRollButtonsDisabled(root, getSpinningSet(root).size > 0);
}

/**
 * @param {HTMLElement} root
 * @param {'brown' | 'white'} player
 */
function clearPlayerSpin(root, player) {
  const map = spinIntervals.get(root);
  const id = map?.get(player);
  if (id != null) {
    window.clearInterval(id);
    map?.delete(player);
  }
  root.querySelectorAll(`[data-die-player="${player}"]`).forEach((slot) => {
    slot.classList.remove("bg-die-spinning");
  });
  getSpinningSet(root).delete(player);
  syncRollButtons(root);
}

export function rollDie() {
  return Math.floor(Math.random() * 6) + 1;
}

/**
 * @param {HTMLElement} root
 * @param {'brown' | 'white'} player
 */
export function isDiceSpinning(root, player) {
  return getSpinningSet(root).has(player);
}

/**
 * @param {HTMLElement} root
 */
export function isAnyDiceSpinning(root) {
  return getSpinningSet(root).size > 0;
}

/**
 * @param {HTMLElement} root
 * @param {'brown' | 'white'} player
 * @param {number} [durationMs]
 */
export function startDiceSpin(root, player, durationMs = DICE_SPIN_MS) {
  clearPlayerSpin(root, player);
  getSpinningSet(root).add(player);
  syncRollButtons(root);

  const slots = root.querySelectorAll(`[data-die-player="${player}"]`);
  slots.forEach((slot) => slot.classList.add("bg-die-spinning"));

  let map = spinIntervals.get(root);
  if (!map) {
    map = new Map();
    spinIntervals.set(root, map);
  }

  const intervalId = window.setInterval(() => {
    slots.forEach((slot) => {
      const el = /** @type {HTMLElement} */ (slot);
      const face = DIE_FACES[Math.floor(Math.random() * 6)];
      el.dataset.face = face;
    });
  }, FACE_CYCLE_MS);
  map.set(player, intervalId);

  void durationMs;
}

/**
 * @param {HTMLElement} root
 * @param {'brown' | 'white'} player
 * @param {[number, number]} values
 */
export function finishDiceSpin(root, player, values) {
  clearPlayerSpin(root, player);
  renderDice(root, player, values);
}

/**
 * @param {HTMLElement} root
 * @param {'brown' | 'white'} player
 * @param {[number, number]} values
 */
export function renderDice(root, player, values) {
  const slots = root.querySelectorAll(`[data-die-player="${player}"]`);
  slots.forEach((slot) => {
    const el = /** @type {HTMLElement} */ (slot);
    const index = Number(el.dataset.dieIndex);
    el.dataset.face = DIE_FACES[values[index] - 1];
    el.setAttribute("aria-label", `${player} die ${index + 1}: ${values[index]}`);
  });
}

/**
 * @param {HTMLElement} root
 * @returns {{ brown: [number, number], white: [number, number] }}
 */
export function getDiceValues(root) {
  const read = (player) => {
    const slots = root.querySelectorAll(`[data-die-player="${player}"]`);
    const vals = [1, 1];
    slots.forEach((slot) => {
      const el = /** @type {HTMLElement} */ (slot);
      const idx = Number(el.dataset.dieIndex);
      const face = el.dataset.face;
      const i = DIE_FACES.indexOf(face);
      vals[idx] = i >= 0 ? i + 1 : 1;
    });
    return /** @type {[number, number]} */ ([vals[0], vals[1]]);
  };
  return { brown: read("brown"), white: read("white") };
}

/**
 * @param {HTMLElement} root
 * @param {'brown' | 'white'} player
 * @param {[number, number]} values
 * @param {number} durationMs
 * @param {() => void} [onComplete]
 */
export function playDiceRollAnimation(root, player, values, durationMs = DICE_SPIN_MS, onComplete) {
  startDiceSpin(root, player, durationMs);
  window.setTimeout(() => {
    finishDiceSpin(root, player, values);
    onComplete?.();
  }, durationMs);
}

/**
 * @param {HTMLElement} root
 * @param {object} [hooks]
 * @param {() => void} [hooks.onChange] Called after roll completes (for DB sync).
 * @param {(player: 'brown' | 'white', values: [number, number], rollId: string) => void} [hooks.onRollStart]
 * @param {(player: 'brown' | 'white', values: [number, number]) => void} [hooks.onRollComplete]
 */
export function bindDiceRollers(root, hooks = {}) {
  root.querySelectorAll("[data-roll-player]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (isAnyDiceSpinning(root)) return;

      const player = /** @type {'brown' | 'white'} */ (/** @type {HTMLElement} */ (btn).dataset.rollPlayer);
      const values = /** @type {[number, number]} */ ([rollDie(), rollDie()]);
      const rollId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;

      hooks.onRollStart?.(player, values, rollId);

      playDiceRollAnimation(root, player, values, DICE_SPIN_MS, () => {
        hooks.onRollComplete?.(player, values);
        hooks.onChange?.();
      });
    });
  });
}
